import { Column, Item } from '../domain/schemas';

import { parseNumber } from './parseNumber';

/**
 * 導入後推移のテーブルデータを「項目絞り込み」用に、推移列だけ絞り込んだコピーを返す。
 *
 * 【Why】
 * - セレクタから渡る `currentFields` は、項目絞り込み UI（`TransitionAfterIntroductionItemSelect`）由来で
 *   **推移列（columnsForTransition）の code だけ**である。非推移は選択肢に含まれない。
 * - 旧実装のように非推移も `currentFields` で filter すると、種別・機種名などが消えたり、
 *   DK-SIS 付きで「SIS 非推移だけマッチ」となり基本情報が欠ける、といった不整合が起きる。
 * - 推移の SIS 列は項目絞り込みの選択肢に載るため、`currentFields` に含まれるときだけ表示し、
 *   外したら消えるのが正しい。列順 state で SIS を維持するのは Saga 側の責務。
 *
 * 【What】
 * - **非推移**: `columnsForNonTransition` と各行の `dataForNonTransition` は **変更しない**。
 * - **推移**: `currentFields` に含まれる code の列（とセル系列）だけ残す。
 * - 絞り結果が空なら推移列は **全列にフォールバック**（従来どおり「選びようがない」扱い）。
 *
 * @param data 導入後推移 API レスポンスに準ずるデータ
 * @param currentFields 項目絞り込みで選択中の推移項目 code（`['all']` のときは呼び出し側で未使用）
 * @returns 絞り込み後のデータ。`data` が undefined のときは undefined
 */
export const filterTransitionDataByFields = <
  T extends {
    data: {
      columnsForTransition: Column[];
      columnsForNonTransition: Column[];
      rows: {
        dataForNonTransition: Item[];
        dataForTransition: Item[][];
      }[];
    };
  }
>(
  data: T | undefined,
  currentFields: string[]
): T | undefined => {
  if (data == null) {
    return undefined;
  }

  const columnsForTransition = data.data.columnsForTransition;
  const columnsForNonTransition = data.data.columnsForNonTransition;

  const currentFieldCodeSet = new Set(currentFields);

  const isTransitionColumnKeptForFieldFilter = (column: Column): boolean =>
    column.code != null && currentFieldCodeSet.has(column.code);

  const filteredForTransitionColumns = columnsForTransition?.filter(
    isTransitionColumnKeptForFieldFilter
  );

  const usesTransitionColumnSubset =
    (filteredForTransitionColumns?.length ?? 0) !== 0;

  const columnForTransitionIndexNums = usesTransitionColumnSubset
    ? columnsForTransition
        ?.map((column, columnIndex) =>
          isTransitionColumnKeptForFieldFilter(column)
            ? String(columnIndex)
            : ''
        )
        .filter(String) ?? []
    : columnsForTransition?.map((_, columnIndex) => String(columnIndex)) ?? [];

  const filteredRows = data.data.rows.map((row) => {
    const { dataForNonTransition, dataForTransition, ...rest } = row;
    return {
      dataForNonTransition,
      dataForTransition: columnForTransitionIndexNums
        .map((index) => parseNumber(index))
        .filter((index) => !isNaN(index))
        .map((index) => dataForTransition[index]),
      ...rest,
    };
  });

  const result = {
    rows: filteredRows,
    columnsForTransition:
      filteredForTransitionColumns?.length !== 0
        ? filteredForTransitionColumns
        : columnsForTransition,
    columnsForNonTransition: columnsForNonTransition,
  };

  if (
    result.rows == null ||
    result.columnsForTransition == null ||
    result.columnsForNonTransition == null
  ) {
    return undefined;
  }

  return {
    ...data,
    data: {
      ...data.data,
      rows: result.rows,
      columnsForTransition: result.columnsForTransition,
      columnsForNonTransition: result.columnsForNonTransition,
    },
  };
};
